<template>
  <nav
    class="theme-bar-wrapper"
    :class="{ 'is--centered': centered }"
    aria-label="Thema's"
    role="navigation"
  >
    <span v-if="heading" class="heading">{{ heading }}</span>
    <Carousel v-if="themes" ref="carouselRef" class="theme-bar">
      <li class="theme-menu-item" :class="{ 'is--active': !activeTheme }">
        <SmartLink
          class="link"
          tag="a"
          :to="firstTheme.slug"
          :label="firstTheme.title"
          @click="() => handleLinkClick(firstTheme.title, firstTheme.slug, 'theme-bar-link')"
        >
          {{ firstTheme.title }}
        </SmartLink>
      </li>
      <li
        v-for="(theme, index) in themes"
        :key="`slide_${theme.title}_${index}`"
        class="slide theme-menu-item"
        :class="{ 'is--active': theme.slug === activeTheme }"
      >
        <SmartLink
          class="link"
          tag="a"
          :to="getThemeUrl(theme.slug)"
          :label="theme.title"
          @click="() => handleLinkClick(theme.title, getThemeUrl(theme.slug), 'theme-bar-link')"
        >
          {{ theme.title }}
        </SmartLink>
      </li>

      <template #navigation-left>
        <Icon name="eenvandaag-general:chevron-left" role="presentation" />
      </template>
      <template #navigation-right>
        <Icon name="eenvandaag-general:chevron-right" role="presentation" />
      </template>
    </Carousel>
  </nav>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import useCollection from '~/composables/useCollection';
import type { ICmsThemes } from '~~/types/_cms-types';
import handleLinkClick from '~/composables/handleLinkClick';

const route = useRoute();
const carouselRef = ref();

interface IProps {
  heading?: string;
  centered?: boolean;
  overviewLabel: string;
}

const props = defineProps<IProps>();

const themes = (await useCollection('themes')) as ICmsThemes[];

const activeTheme = ref();
const firstTheme = ref();

const isOpinion = route.path.startsWith('/opiniepanel');

firstTheme.value = {
  slug: isOpinion ? `/opiniepanel/` : '/',
  title: props.overviewLabel,
};

const getThemeUrl = (slug: string | undefined) => {
  if (!slug) return '/themas/';
  if (isOpinion) return `/opiniepanel/themas/${slug}`;
  return '/themas/' + slug;
};

// Extract theme from the path
const extractThemeFromPath = (path: string) => {
  const themeMatch = path.match(/\/themas\/([^/]+)/);
  return themeMatch ? themeMatch[1] : null;
};

const getActiveItemIndex = (): number => {
  return themes.findIndex((theme) => theme.slug === activeTheme.value);
};

// set activeTheme
activeTheme.value = extractThemeFromPath(route.path) || null;

const update = (newPath: string) => {
  if (themes.length === 0) return;

  // prevent switch from / to /opiniepanal from triggering update on /
  if (newPath !== firstTheme.value.slug && !newPath.startsWith(`${firstTheme.value.slug}themas`))
    return;

  activeTheme.value = extractThemeFromPath(newPath) || null;
  const item = getActiveItemIndex() as number;
  carouselRef.value?.goToItem(item);
};

watch(
  () => route.path,
  (newPath) => {
    update(newPath);
  },
  { immediate: true },
);

onMounted(() => {
  nextTick(() => {
    update(route.path);
  });
});
</script>

<style scoped lang="postcss">
.heading {
  @add-mixin heading-3xl;
  color: var(--color-primary-950);
}

.theme-bar-wrapper {
  --theme-bar-wrapper-padding: var(--unit-16) 0 0 0;
  --theme-bar-wrapper-gap: var(--unit-16);
  display: flex;
  flex-direction: column;
  gap: var(--theme-bar-wrapper-gap);

  width: 100%;
  position: relative;
  max-width: var(--global-site-width);
  margin: 0 auto;
  padding: var(--theme-bar-wrapper-padding);

  &.is--centered {
    --theme-bar-wrapper-padding: 0;
  }

  @media (--lg-viewport) {
    --theme-bar-wrapper-padding: var(--unit-24) 0 0;
    --theme-bar-wrapper-gap: var(--unit-24);

    &.is--centered {
      &:deep(.carousel-track) {
        justify-content: center;
      }
    }
  }
}

.theme-bar {
  width: 100%;
  position: relative;
  max-width: var(--global-site-max-width);

  @media (--lg-viewport) {
    overflow: visible;
  }

  &:deep(.carousel-track) {
    gap: 0;

    @media (--md-viewport) {
      gap: var(--unit-4);
    }
  }

  &:deep(.is--masked-gradient) {
    @media (--sm-viewport) {
      &.is--masked-gradient {
        mask-position:
          0 0,
          100% 0;
        mask-repeat: no-repeat, no-repeat;

        &.is--active-both {
          mask-image: linear-gradient(
            90deg,
            transparent 0%,
            transparent 10%,
            white 15%,
            white 85%,
            transparent 90%,
            transparent 100%
          );
        }

        &.is--active-next {
          mask-image: linear-gradient(
            90deg,
            white 0%,
            white 85%,
            transparent 90%,
            transparent 100%
          );
        }

        &.is--active-prev {
          mask-image: linear-gradient(
            90deg,
            transparent 0%,
            transparent 10%,
            white 15%,
            white 100%
          );
        }
      }
    }
  }

  &:deep(.carousel-nav-arrow) {
    color: hsl(var(--color-primary-300));
    width: 40px;
    height: 40px;
    padding: 0;
    margin: 0;
    font-size: 22px;
    border-radius: 0;
    box-shadow: none;
    background-color: transparent;
    translate: 0 -50%;

    &.right-arrow,
    &.left-arrow {
      translate: 0 -50%;
    }

    @media (--sm-viewport) {
      display: flex;
    }
  }
}

.theme-menu-item {
  flex: 0 0 auto;
  cursor: pointer;
  padding: var(--unit-4) var(--unit-10);
  text-align: center;

  transition: color 200ms ease-in-out;

  @add-mixin label-lg-semi-bold;

  .link {
    outline: none;
    color: hsl(var(--color-primary-950));

    @add-mixin focus light;
  }

  @add-mixin hover {
    color: hsl(var(--color-primary-700));
    > .link {
      color: hsl(var(--color-primary-700));
    }
  }

  &:active {
    color: hsl(var(--color-primary-800));
    > .link {
      color: hsl(var(--color-primary-800));
    }
  }
}

.theme-menu-item.is--active {
  background-color: hsl(var(--color-primary-100));
  border-radius: var(--unit-4);
}
</style>
